// src/components/OrgManagementPage.js

import React, { useState, useEffect, useCallback } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBuilding, faTrashAlt, faCheckCircle, faExclamationTriangle,
  faTimesCircle, faShieldAlt, faKeyboard, faUserSlash, faUsersCog,
  faImages, faArrowLeft, faSpinner, faBirthdayCake, faRing, faMapMarkerAlt,
  faSearchLocation, faChartBar, faChevronDown, faChevronUp, faUsers,
  faUserFriends, faHome, faLayerGroup, faBell, faMobileAlt, faPhone, faUserCircle,
  faEnvelope, faLink, faIdBadge
} from '@fortawesome/free-solid-svg-icons';
import { Modal, Button, Form } from 'react-bootstrap';

import { functions } from '../firebase';
import { Timestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '../context/AuthContext';

import './OrgManagementPage.css';

// Callable function references
const getAllOrganizationsCallable = httpsCallable(functions, 'getAllOrganizations');
const deleteAllDataCallable = httpsCallable(functions, 'deleteAllDataForOrganization');
const deleteOrphanedMembersCallable = httpsCallable(functions, 'deleteOrphanedMembersByFamily');
const batchSyncClaimsAndUsersCallable = httpsCallable(functions, 'batchSyncClaimsAndFirestoreUsers');
const backfillFaceProcessingCallable = httpsCallable(functions, 'backfillFaceProcessing');
const cleanUpBirthdayDataCallable = httpsCallable(functions, 'cleanUpBirthdayDataForOrg');
const migrateAnniversaryFieldsCallable = httpsCallable(functions, 'migrateAnniversaryFieldsToCamelCase');
const backfillFamilyGeocodingCallable = httpsCallable(functions, 'backfillFamilyGeocoding');
const auditPhotoPathsCallable = httpsCallable(functions, 'auditPhotoPaths');
const getAllOrgStatsCallable = httpsCallable(functions, 'getAllOrgStats');
const backfillHasCellphoneCallable = httpsCallable(functions, 'backfillHasCellphone');
const backfillMemberPhotosCallable = httpsCallable(functions, 'backfillMemberPhotos');
const migrateAutomationTemplatesCallable = httpsCallable(functions, 'migrateAutomationTemplates');
const backfillNewsletterSubscriberMemberIdsCallable = httpsCallable(functions, 'backfillNewsletterSubscriberMemberIds');
const backfillNewsletterSubscriberNamesCallable = httpsCallable(functions, 'backfillNewsletterSubscriberNames');

const AUTHORIZED_EMAIL = "jonstewart24@gmail.com";

// Scalable Action Configuration
const ORG_ACTIONS = [
  {
    id: 'migrateAnniversaries',
    label: 'Anniversaries',
    icon: faRing,
    variant: 'primary',
    confirmMessage: (org) => `Are you sure you want to migrate anniversary data for organization: ${org.orgName || org.id}?\n\nThis will process 'family' records to copy snake_case anniversary fields to new camelCase fields.`,
  },
  {
    id: 'cleanBirthdays',
    label: 'Birthdays',
    icon: faBirthdayCake,
    variant: 'secondary',
    confirmMessage: (org) => `Are you sure you want to clean up birthday data for organization: ${org.orgName || org.id}?\n\nThis will process 'member' records to standardize birthday fields.`,
  },
  {
    id: 'cleanMembers',
    label: 'Orphans',
    icon: faUserSlash,
    variant: 'info',
    confirmMessage: (org) => `Are you sure you want to clean up orphaned members for organization: ${org.orgName || org.id}?\n\nThis will delete members whose familyId points to a non-existent family.`,
  },
  {
    id: 'backfillFaces',
    label: 'Faces',
    icon: faImages,
    variant: 'success',
    confirmMessage: (org) => `Are you sure you want to run photo standardization & face backfill for organization: ${org.orgName || org.id}?\n\nThis will check family photos and trigger face extraction.`,
  },
  {
    id: 'backfillGeocoding',
    label: 'Geocode',
    icon: faMapMarkerAlt,
    variant: 'primary',
    confirmMessage: (org) => `Are you sure you want to backfill geocoding for organization: ${org.orgName || org.id}?\n\nThis will geocode family addresses that are missing lat/lng coordinates.`,
  },
  {
    id: 'auditPhotoPaths',
    label: 'Audit Photos',
    icon: faSearchLocation,
    variant: 'warning',
    confirmMessage: (org) => `Run photo path audit for organization: ${org.orgName || org.id}?\n\nThis will check all family and member photos for non-standard paths.`,
  },
  {
    id: 'backfillCellphone',
    label: 'Cellphones',
    icon: faPhone,
    variant: 'secondary',
    confirmMessage: (org) => `Backfill hasCellphone field for organization: ${org.orgName || org.id}?\n\nThis will set hasCellphone=true for members with cellphones and hasCellphone=false for those without.`,
  },
  {
    id: 'backfillMemberPhotos',
    label: 'User Photos',
    icon: faUserCircle,
    variant: 'info',
    confirmMessage: (org) => `Backfill member photos to user profiles for organization: ${org.orgName || org.id}?\n\nThis will sync member photos to linked user documents so they appear as profile avatars.`,
  },
  {
    id: 'migrateTemplates',
    label: 'Templates',
    icon: faEnvelope,
    variant: 'primary',
    confirmMessage: (org) => `Update automation email templates for organization: ${org.orgName || org.id}?\n\nThis will overwrite the birthday, anniversary, and welcome message subject/body with the latest templates.`,
  },
  {
    id: 'backfillNewsletterMembers',
    label: 'Newsletter Links',
    icon: faLink,
    variant: 'info',
    confirmMessage: (org) => `Backfill member links for newsletter subscribers in organization: ${org.orgName || org.id}?\n\nThis will match existing subscribers to member records by email and set memberId where found.`,
  },
  {
    id: 'backfillNewsletterNames',
    label: 'Split Names',
    icon: faIdBadge,
    variant: 'secondary',
    confirmMessage: (org) => `Split legacy subscriber names into first/last name for organization: ${org.orgName || org.id}?\n\nThis will split each subscriber's old "name" field into firstName/lastName and remove the old field.`,
  },
  {
    id: 'deleteOrg',
    label: 'Delete',
    icon: faTrashAlt,
    variant: 'danger',
    requiresModal: true,
  },
];

function OrgManagementPage() {
  const { currentUser, loadingAuth } = useAuth();

  const [organizations, setOrganizations] = useState([]);
  const [loadingOrgs, setLoadingOrgs] = useState(true);
  const [actionError, setActionError] = useState(null);
  const [userMessage, setUserMessage] = useState({ type: '', text: '', details: null });

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [orgToDelete, setOrgToDelete] = useState(null);
  const [confirmInput, setConfirmInput] = useState("");
  const [manualOrgIdInput, setManualOrgIdInput] = useState("");

  // Action loading states - using a map for scalability
  const [loadingActions, setLoadingActions] = useState({});

  // Statistics state
  const [orgStats, setOrgStats] = useState([]);
  const [loadingStats, setLoadingStats] = useState(false);
  const [statsError, setStatsError] = useState(null);
  const [expandedStatsOrg, setExpandedStatsOrg] = useState(null);

  const setActionLoading = (actionId, orgId, isLoading) => {
    setLoadingActions(prev => ({
      ...prev,
      [`${actionId}-${orgId || 'global'}`]: isLoading
    }));
  };

  const isActionLoading = (actionId, orgId) => {
    return loadingActions[`${actionId}-${orgId || 'global'}`] || false;
  };

  const isAnyActionLoading = Object.values(loadingActions).some(v => v);

  const formatDate = (dateValue) => {
    if (!dateValue) return 'N/A';
    try {
      let date;
      if (dateValue instanceof Timestamp) {
        date = dateValue.toDate();
      } else if (typeof dateValue === 'string') {
        date = new Date(dateValue);
      } else if (dateValue && typeof dateValue._seconds === 'number') {
        date = new Timestamp(dateValue._seconds, dateValue._nanoseconds).toDate();
      } else {
        date = new Date(dateValue);
      }
      if (isNaN(date.getTime())) return 'Invalid Date';
      return date.toLocaleDateString();
    } catch (e) {
      return 'Invalid Date';
    }
  };

  const fetchOrganizations = useCallback(async () => {
    if (currentUser?.email?.toLowerCase() !== AUTHORIZED_EMAIL.toLowerCase()) {
      setLoadingOrgs(false);
      setActionError("Unauthorized to fetch organizations.");
      return;
    }
    setLoadingOrgs(true);
    setActionError(null);
    setUserMessage({ type: '', text: '', details: null });

    try {
      const result = await getAllOrganizationsCallable();
      const data = result.data;
      if (data && data.success) {
        setOrganizations(data.organizations || []);
      } else {
        throw new Error(data?.message || "Failed to fetch organizations.");
      }
    } catch (err) {
      console.error("Error fetching organizations:", err);
      setActionError(err.message || "An error occurred while fetching organizations.");
      setOrganizations([]);
    } finally {
      setLoadingOrgs(false);
    }
  }, [currentUser?.email]);

  useEffect(() => {
    if (!loadingAuth && currentUser) {
      fetchOrganizations();
    } else if (!loadingAuth && !currentUser) {
      setLoadingOrgs(false);
      setActionError("User not authenticated.");
    }
  }, [loadingAuth, currentUser, fetchOrganizations]);

  // --- Action Handlers ---
  const handleOrgAction = async (actionId, org) => {
    const action = ORG_ACTIONS.find(a => a.id === actionId);
    if (!action) return;

    if (action.requiresModal) {
      setOrgToDelete({ id: org.id, orgName: org.orgName || `Org ID: ${org.id}` });
      setConfirmInput("");
      setShowConfirmModal(true);
      return;
    }

    if (action.confirmMessage && !window.confirm(action.confirmMessage(org))) {
      return;
    }

    setActionLoading(actionId, org.id, true);
    setUserMessage({ type: '', text: '', details: null });
    setActionError(null);

    try {
      let result;
      switch (actionId) {
        case 'migrateAnniversaries':
          result = await migrateAnniversaryFieldsCallable({ orgId: org.id });
          break;
        case 'cleanBirthdays':
          result = await cleanUpBirthdayDataCallable({ orgId: org.id });
          break;
        case 'cleanMembers':
          result = await deleteOrphanedMembersCallable({ orgId: org.id });
          break;
        case 'backfillFaces':
          result = await backfillFaceProcessingCallable({ orgId: org.id, dryRun: false });
          break;
        case 'backfillGeocoding':
          result = await backfillFamilyGeocodingCallable({ orgId: org.id, dryRun: false });
          break;
        case 'auditPhotoPaths':
          result = await auditPhotoPathsCallable({ orgId: org.id });
          break;
        case 'backfillCellphone':
          result = await backfillHasCellphoneCallable({ orgId: org.id, dryRun: false });
          break;
        case 'backfillMemberPhotos':
          result = await backfillMemberPhotosCallable({ orgId: org.id, dryRun: false });
          break;
        case 'migrateTemplates':
          result = await migrateAutomationTemplatesCallable({ orgId: org.id, dryRun: false });
          break;
        case 'backfillNewsletterMembers':
          result = await backfillNewsletterSubscriberMemberIdsCallable({ orgId: org.id, dryRun: false });
          break;
        case 'backfillNewsletterNames':
          result = await backfillNewsletterSubscriberNamesCallable({ orgId: org.id, dryRun: false });
          break;
        default:
          throw new Error('Unknown action');
      }

      const data = result.data;
      if (data.success !== false) {
        const hasIssues = data.issuesFound > 0 || data.summary?.errors > 0 || data.summary?.issues?.length > 0;
        setUserMessage({
          type: hasIssues ? 'warning' : 'success',
          text: `[${org.orgName || org.id}] ${data.message || 'Action completed successfully.'}`,
          details: data.issues || data.summary?.issues || null
        });
      } else {
        throw new Error(data.message || 'Action failed.');
      }
    } catch (err) {
      console.error(`Error with action ${actionId} for org ${org.id}:`, err);
      setUserMessage({
        type: 'danger',
        text: `[${org.orgName || org.id}] Error: ${err.message || 'An unknown error occurred.'}`
      });
    } finally {
      setActionLoading(actionId, org.id, false);
    }
  };

  const handleBatchSync = async () => {
    if (!window.confirm("Are you sure you want to run the batch process to sync Auth claims and Firestore 'users' records for ALL users?\n\nThis operation may take several minutes.")) {
      return;
    }

    setActionLoading('batchSync', 'global', true);
    setUserMessage({ type: '', text: '', details: null });

    try {
      const result = await batchSyncClaimsAndUsersCallable();
      const data = result.data;
      if (data.success) {
        setUserMessage({
          type: 'success',
          text: `Batch sync successful! ${data.message}`
        });
      } else {
        throw new Error(data.message || "Batch sync failed.");
      }
    } catch (err) {
      console.error("Error with batch sync:", err);
      setUserMessage({ type: 'danger', text: `Batch sync failed: ${err.message}` });
    } finally {
      setActionLoading('batchSync', 'global', false);
    }
  };

  const handleGlobalPhotoAudit = async () => {
    if (!window.confirm("Run photo path audit for ALL organizations?\n\nThis will check all family and member photos across all orgs for non-standard paths.")) {
      return;
    }

    setActionLoading('auditPhotoPaths', 'global', true);
    setUserMessage({ type: '', text: '', details: null });

    try {
      const result = await auditPhotoPathsCallable({});
      const data = result.data;
      const hasIssues = data.summary?.issues?.length > 0;
      setUserMessage({
        type: hasIssues ? 'warning' : 'success',
        text: data.message || 'Photo audit completed.',
        details: data.summary?.issues || null
      });
    } catch (err) {
      console.error("Error with global photo audit:", err);
      setUserMessage({ type: 'danger', text: `Photo audit failed: ${err.message}` });
    } finally {
      setActionLoading('auditPhotoPaths', 'global', false);
    }
  };

  const handleInitiateManualDelete = () => {
    if (!manualOrgIdInput.trim()) {
      setUserMessage({ type: 'warning', text: 'Please enter an Organization ID.' });
      return;
    }
    setOrgToDelete({ id: manualOrgIdInput.trim(), orgName: `Org ID: ${manualOrgIdInput.trim()}` });
    setConfirmInput("");
    setShowConfirmModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!orgToDelete || confirmInput !== orgToDelete.id) {
      setUserMessage({ type: 'warning', text: 'Confirmation text does not match.' });
      return;
    }

    setActionLoading('deleteOrg', orgToDelete.id, true);
    setUserMessage({ type: '', text: '', details: null });

    try {
      const result = await deleteAllDataCallable({ orgId: orgToDelete.id });
      const data = result.data;
      if (data.success) {
        setUserMessage({
          type: 'success',
          text: `Successfully deleted all data for: ${orgToDelete.orgName}`
        });
        setManualOrgIdInput("");
        fetchOrganizations();
      } else {
        throw new Error(data.message || 'Deletion failed.');
      }
    } catch (err) {
      console.error(`Error deleting org ${orgToDelete.id}:`, err);
      setUserMessage({ type: 'danger', text: `Deletion failed: ${err.message}` });
    } finally {
      setActionLoading('deleteOrg', orgToDelete.id, false);
      setShowConfirmModal(false);
      setOrgToDelete(null);
    }
  };

  // --- Statistics Handler ---
  const handleLoadAllStats = async () => {
    setLoadingStats(true);
    setStatsError(null);
    setOrgStats([]);

    try {
      const result = await getAllOrgStatsCallable();
      const data = result.data;
      if (data.success) {
        setOrgStats(data.stats || []);
      } else {
        throw new Error(data.message || 'Failed to fetch statistics.');
      }
    } catch (err) {
      console.error("Error fetching org stats:", err);
      setStatsError(err.message || 'An error occurred while fetching statistics.');
    } finally {
      setLoadingStats(false);
    }
  };

  const toggleStatsExpand = (orgId) => {
    setExpandedStatsOrg(expandedStatsOrg === orgId ? null : orgId);
  };

  // --- Render Logic ---
  if (loadingAuth) {
    return (
      <div className="org-management-page">
        <div className="org-management-loading">
          <div className="skeleton-header"></div>
          <div className="skeleton-card"></div>
          <div className="skeleton-card"></div>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  if (currentUser.email?.toLowerCase() !== AUTHORIZED_EMAIL.toLowerCase()) {
    return (
      <div className="org-management-page">
        <div className="org-management-header">
          <div className="container">
            <div className="org-management-header-content">
              <div className="org-management-header-left">
                <Link to="/dashboard" className="org-management-back-btn">
                  <FontAwesomeIcon icon={faArrowLeft} />
                </Link>
                <h1 className="org-management-title">Access Denied</h1>
              </div>
            </div>
          </div>
        </div>
        <div className="container">
          <div className="org-alert org-alert-danger">
            <FontAwesomeIcon icon={faExclamationTriangle} className="org-alert-icon" />
            <div className="org-alert-content">You do not have permission to view this page.</div>
          </div>
          <Link to="/dashboard" className="org-action-btn org-action-btn-warning">
            Go to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="org-management-page">
      {/* Page Header */}
      <div className="org-management-header">
        <div className="container">
          <div className="org-management-header-content">
            <div className="org-management-header-left">
              <Link to="/dashboard" className="org-management-back-btn" title="Back to Dashboard">
                <FontAwesomeIcon icon={faArrowLeft} />
              </Link>
              <div>
                <h1 className="org-management-title">
                  <FontAwesomeIcon icon={faShieldAlt} style={{ marginRight: '0.5rem' }} />
                  Organization Management
                </h1>
                <p className="org-management-subtitle">Super admin utilities and data management</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container">
        <div className="org-management-content">
          {/* Alerts */}
          {userMessage.text && (
            <div className={`org-alert org-alert-${userMessage.type || 'info'}`}>
              {userMessage.type === 'success' && <FontAwesomeIcon icon={faCheckCircle} className="org-alert-icon" />}
              {(userMessage.type === 'danger' || userMessage.type === 'warning') && <FontAwesomeIcon icon={faExclamationTriangle} className="org-alert-icon" />}
              <div className="org-alert-content">
                {userMessage.text}
                {userMessage.details && userMessage.details.length > 0 && (
                  <div className="org-alert-issues">
                    {userMessage.details.map((issue, index) => (
                      <div key={index} className="org-alert-issue-item">
                        {issue.documentId ? (
                          <><strong>Doc:</strong> {issue.documentId} - {issue.reason}</>
                        ) : issue.type ? (
                          <>
                            <strong>[{issue.type.toUpperCase()}]</strong> {issue.id}: {issue.issue}
                            <div className="org-alert-issue-path">
                              <code>{issue.currentPath}</code>
                            </div>
                          </>
                        ) : (
                          <>{JSON.stringify(issue)}</>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <button className="org-alert-close" onClick={() => setUserMessage({ type: '', text: '', details: null })}>
                &times;
              </button>
            </div>
          )}

          {actionError && !userMessage.text && (
            <div className="org-alert org-alert-danger">
              <FontAwesomeIcon icon={faExclamationTriangle} className="org-alert-icon" />
              <div className="org-alert-content">{actionError}</div>
            </div>
          )}

          {/* Global Actions */}
          <div className="org-global-actions">
            {/* Batch Sync Card */}
            <div className="org-action-card">
              <div className="org-action-card-header">
                <div className="org-action-card-icon org-action-card-icon-warning">
                  <FontAwesomeIcon icon={faUsersCog} />
                </div>
                <h3 className="org-action-card-title">Batch Sync Users & Claims</h3>
              </div>
              <div className="org-action-card-body">
                <p className="org-action-card-description">
                  Sync Firebase Auth claims and Firestore user records for all users. Sets orgId, userMid, userFid claims and creates/updates user documents.
                </p>
                <button
                  className="org-action-btn org-action-btn-warning"
                  onClick={handleBatchSync}
                  disabled={isAnyActionLoading}
                >
                  {isActionLoading('batchSync', 'global') ? (
                    <><FontAwesomeIcon icon={faSpinner} spin /> Syncing...</>
                  ) : (
                    <><FontAwesomeIcon icon={faUsersCog} /> Run Batch Sync</>
                  )}
                </button>
              </div>
            </div>

            {/* Global Photo Audit Card */}
            <div className="org-action-card">
              <div className="org-action-card-header">
                <div className="org-action-card-icon org-action-card-icon-info">
                  <FontAwesomeIcon icon={faSearchLocation} />
                </div>
                <h3 className="org-action-card-title">Audit Photo Paths (All Orgs)</h3>
              </div>
              <div className="org-action-card-body">
                <p className="org-action-card-description">
                  Scan all organizations for family and member photos with non-standard storage paths. Identifies photos that won't work with the resize extension.
                </p>
                <button
                  className="org-action-btn org-action-btn-info"
                  onClick={handleGlobalPhotoAudit}
                  disabled={isAnyActionLoading}
                >
                  {isActionLoading('auditPhotoPaths', 'global') ? (
                    <><FontAwesomeIcon icon={faSpinner} spin /> Auditing...</>
                  ) : (
                    <><FontAwesomeIcon icon={faSearchLocation} /> Run Photo Audit</>
                  )}
                </button>
              </div>
            </div>

            {/* Manual Delete Card */}
            <div className="org-action-card">
              <div className="org-action-card-header">
                <div className="org-action-card-icon org-action-card-icon-danger">
                  <FontAwesomeIcon icon={faKeyboard} />
                </div>
                <h3 className="org-action-card-title">Manual Organization Deletion</h3>
              </div>
              <div className="org-action-card-body">
                <p className="org-action-card-description">
                  Delete all data for an organization by ID. Use for orgs not listed below or already partially deleted.
                </p>
                <div className="org-action-card-input">
                  <input
                    type="text"
                    placeholder="Enter Organization ID"
                    value={manualOrgIdInput}
                    onChange={(e) => setManualOrgIdInput(e.target.value)}
                  />
                  <button
                    className="org-action-btn org-action-btn-outline-danger"
                    onClick={handleInitiateManualDelete}
                    disabled={!manualOrgIdInput.trim() || isAnyActionLoading}
                  >
                    <FontAwesomeIcon icon={faTrashAlt} /> Delete
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Organizations Table */}
          <div className="org-table-section">
            <div className="org-table-header">
              <h3 className="org-table-title">
                <FontAwesomeIcon icon={faBuilding} />
                Organizations
                <span className="org-table-count">{organizations.length}</span>
              </h3>
            </div>

            {loadingOrgs ? (
              <div className="org-loading-state">
                <div className="org-loading-spinner"></div>
                <span>Loading organizations...</span>
              </div>
            ) : organizations.length === 0 ? (
              <div className="org-empty-state">
                <div className="org-empty-icon">
                  <FontAwesomeIcon icon={faBuilding} />
                </div>
                <h4>No Organizations</h4>
                <p>No organizations found or you may not be authorized to view them.</p>
              </div>
            ) : (
              <div className="org-table-body">
                <table className="org-table">
                  <thead>
                    <tr>
                      <th>Organization</th>
                      <th>Contact</th>
                      <th>Created</th>
                      <th>Expires</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {organizations.map((org) => (
                      <tr key={org.id}>
                        <td>
                          <div className="org-table-name">{org.orgName || 'Unnamed'}</div>
                          <div className="org-table-id">{org.id}</div>
                        </td>
                        <td>
                          <div>{org.contactName || '-'}</div>
                          <div className="org-table-email">{org.contactEmail || '-'}</div>
                        </td>
                        <td className="org-table-date">{formatDate(org.cDate)}</td>
                        <td className="org-table-date">{formatDate(org.expire)}</td>
                        <td>
                          <div className="org-row-actions">
                            {ORG_ACTIONS.map(action => (
                              <button
                                key={action.id}
                                className={`org-row-action-btn org-row-action-btn-${action.variant}`}
                                title={`${action.label} - ${org.orgName || org.id}`}
                                onClick={() => handleOrgAction(action.id, org)}
                                disabled={isActionLoading(action.id, org.id) || isAnyActionLoading}
                              >
                                {isActionLoading(action.id, org.id) ? (
                                  <FontAwesomeIcon icon={faSpinner} spin />
                                ) : (
                                  <FontAwesomeIcon icon={action.icon} />
                                )}
                                <span>{action.label}</span>
                              </button>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Organization Statistics Section */}
          <div className="org-table-section" style={{ marginTop: '2rem' }}>
            <div className="org-table-header">
              <h3 className="org-table-title">
                <FontAwesomeIcon icon={faChartBar} />
                Organization Statistics
                {orgStats.length > 0 && <span className="org-table-count">{orgStats.length}</span>}
              </h3>
              <button
                className="org-action-btn org-action-btn-primary"
                onClick={handleLoadAllStats}
                disabled={loadingStats || isAnyActionLoading}
                style={{ marginLeft: 'auto' }}
              >
                {loadingStats ? (
                  <><FontAwesomeIcon icon={faSpinner} spin /> Loading Stats...</>
                ) : (
                  <><FontAwesomeIcon icon={faChartBar} /> Load All Stats</>
                )}
              </button>
            </div>

            {statsError && (
              <div className="org-alert org-alert-danger" style={{ marginTop: '1rem' }}>
                <FontAwesomeIcon icon={faExclamationTriangle} className="org-alert-icon" />
                <div className="org-alert-content">{statsError}</div>
              </div>
            )}

            {loadingStats ? (
              <div className="org-loading-state">
                <div className="org-loading-spinner"></div>
                <span>Loading statistics for all organizations...</span>
              </div>
            ) : orgStats.length === 0 ? (
              <div className="org-empty-state" style={{ padding: '2rem' }}>
                <div className="org-empty-icon">
                  <FontAwesomeIcon icon={faChartBar} />
                </div>
                <h4>No Statistics Loaded</h4>
                <p>Click "Load All Stats" to fetch organization statistics.</p>
              </div>
            ) : (
              <div className="org-table-body">
                <table className="org-table">
                  <thead>
                    <tr>
                      <th>Organization</th>
                      <th style={{ textAlign: 'center' }}><FontAwesomeIcon icon={faUsers} title="Users" /> Users</th>
                      <th style={{ textAlign: 'center' }}><FontAwesomeIcon icon={faUserFriends} title="Members" /> Members</th>
                      <th style={{ textAlign: 'center' }}><FontAwesomeIcon icon={faHome} title="Families" /> Families</th>
                      <th style={{ textAlign: 'center' }}><FontAwesomeIcon icon={faLayerGroup} title="Groups" /> Groups</th>
                      <th style={{ textAlign: 'center' }}><FontAwesomeIcon icon={faMobileAlt} title="Users with Tokens" /> Tokens</th>
                      <th style={{ textAlign: 'center' }}><FontAwesomeIcon icon={faBell} title="Users with Prefs Enabled" /> Prefs On</th>
                      <th style={{ textAlign: 'center' }}>Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orgStats.map((stat) => (
                      <React.Fragment key={stat.orgId}>
                        <tr>
                          <td>
                            <div className="org-table-name">{stat.orgName || 'Unnamed'}</div>
                            <div className="org-table-id">{stat.orgId}</div>
                          </td>
                          <td style={{ textAlign: 'center' }}>{stat.userCount}</td>
                          <td style={{ textAlign: 'center' }}>{stat.memberCount}</td>
                          <td style={{ textAlign: 'center' }}>{stat.familyCount}</td>
                          <td style={{ textAlign: 'center' }}>{stat.groupCount}</td>
                          <td style={{ textAlign: 'center' }}>
                            <span style={{
                              color: stat.usersWithTokens > 0 ? '#10b981' : '#6b7280',
                              fontWeight: stat.usersWithTokens > 0 ? '600' : '400'
                            }}>
                              {stat.usersWithTokens}/{stat.userCount}
                            </span>
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <span style={{
                              color: stat.usersWithPrefsEnabled > 0 ? '#3b82f6' : '#6b7280',
                              fontWeight: stat.usersWithPrefsEnabled > 0 ? '600' : '400'
                            }}>
                              {stat.usersWithPrefsEnabled}/{stat.userCount}
                            </span>
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <button
                              className="org-row-action-btn org-row-action-btn-info"
                              onClick={() => toggleStatsExpand(stat.orgId)}
                              title="View preference breakdown"
                            >
                              <FontAwesomeIcon icon={expandedStatsOrg === stat.orgId ? faChevronUp : faChevronDown} />
                              <span>View</span>
                            </button>
                          </td>
                        </tr>
                        {expandedStatsOrg === stat.orgId && (
                          <tr className="org-stats-expanded-row">
                            <td colSpan="8" style={{ backgroundColor: '#f8fafc', padding: '1rem' }}>
                              <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
                                <div>
                                  <strong style={{ color: '#475569', fontSize: '0.875rem' }}>Notification Preference Breakdown:</strong>
                                  <div style={{ marginTop: '0.5rem', display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.5rem 2rem' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
                                      <span style={{ color: '#64748b' }}>Birthday/Anniversary:</span>
                                      <strong>{stat.preferenceBreakdown.birthdayAnniversaryNotifications}</strong>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
                                      <span style={{ color: '#64748b' }}>Events:</span>
                                      <strong>{stat.preferenceBreakdown.eventNotifications}</strong>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
                                      <span style={{ color: '#64748b' }}>Messages:</span>
                                      <strong>{stat.preferenceBreakdown.messageNotifications}</strong>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
                                      <span style={{ color: '#64748b' }}>New Members:</span>
                                      <strong>{stat.preferenceBreakdown.newMemberNotifications}</strong>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {orgToDelete && (
        <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered backdrop="static">
          <Modal.Header closeButton>
            <Modal.Title>
              <FontAwesomeIcon icon={faExclamationTriangle} className="text-danger me-2" />
              Confirm Deletion
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>You are about to delete <strong>ALL</strong> data for:</p>
            <p><strong>{orgToDelete.orgName}</strong></p>
            <p className="text-muted small"><code>{orgToDelete.id}</code></p>
            <p className="text-danger">
              <strong>This action is irreversible.</strong> All families, members, groups, roles, and users will be permanently deleted.
            </p>
            <hr />
            <Form.Group>
              <Form.Label>Type the Organization ID to confirm:</Form.Label>
              <Form.Control
                type="text"
                value={confirmInput}
                onChange={(e) => setConfirmInput(e.target.value)}
                placeholder={orgToDelete.id}
                autoFocus
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowConfirmModal(false)} disabled={isActionLoading('deleteOrg', orgToDelete.id)}>
              <FontAwesomeIcon icon={faTimesCircle} className="me-1" /> Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleConfirmDelete}
              disabled={isActionLoading('deleteOrg', orgToDelete.id) || confirmInput !== orgToDelete.id}
            >
              {isActionLoading('deleteOrg', orgToDelete.id) ? (
                <><FontAwesomeIcon icon={faSpinner} spin className="me-1" /> Deleting...</>
              ) : (
                <><FontAwesomeIcon icon={faTrashAlt} className="me-1" /> Delete All Data</>
              )}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
}

export default OrgManagementPage;
